<template>
    <div>
        <hr>
        <h2 class="ix-page-title-secondary mt-4">Numéros de série</h2>

        <!-- Zone de scan -->
        <div class="sn-scan-zone">
            <div class="input-group input-group-lg">
                <input
                    ref="scanInput"
                    type="text"
                    class="form-control sn-scan-input"
                    :class="{ 'is-invalid': scanError }"
                    v-model="scanValue"
                    placeholder="Scanner ou saisir un numéro de série..."
                    @keydown.enter.prevent="addScannedSerial"
                    :disabled="!snEnabled"
                >
                <div class="input-group-append">
                    <button type="button" class="ix-btn blue" @click="addScannedSerial" :disabled="!scanValue.trim()">
                        <i class="far fa-plus mr-1"></i> Ajouter
                    </button>
                </div>
            </div>
            <div v-if="scanError" class="sn-scan-error">
                <i class="far fa-exclamation-circle mr-1"></i> {{ scanError }}
            </div>
            <small v-else class="text-muted d-block mt-1">Validez avec Entrée pour ajouter le numéro de série scanné.</small>
        </div>

        <!-- Alerte stock -->
        <div v-if="snEnabled && stockValue <= 0" class="alert alert-warning small mb-0 mt-2">
            <i class="far fa-exclamation-triangle mr-1"></i> Veuillez saisir une quantité supérieure à 0 pour ajouter des numéros de série.
        </div>

        <!-- Barre de progression -->
        <div v-if="snEnabled && stockValue > 0" class="sn-progress-wrapper">
            <div class="d-flex align-items-center justify-content-between mb-1">
                <span class="text-muted small font-weight-medium">
                    Progression : {{ serialNumbers.length }} / {{ Math.floor(stockValue) }}
                </span>
                <span class="text-muted small">{{ Math.round(progressPercent) }}%</span>
            </div>
            <div class="progress" style="height: 8px;">
                <div class="progress-bar" :class="progressBarClass" :style="{ width: progressPercent + '%' }"></div>
            </div>
        </div>

        <!-- Alerte si trop de SN -->
        <div v-if="stockValue > 0 && serialNumbers.length > Math.floor(stockValue)" class="alert alert-warning small mb-2 mt-2 py-1">
            <i class="far fa-exclamation-triangle mr-1"></i> Vous avez saisi plus de numéros de série que la quantité indiquée ({{ Math.floor(stockValue) }}).
        </div>

        <!-- Erreurs backend générales -->
        <div v-if="generalErrors.length > 0" class="sn-backend-errors mt-2">
            <div v-for="err in generalErrors" :key="err" class="sn-backend-error-item">
                <i class="far fa-exclamation-circle mr-1"></i> {{ err }}
            </div>
        </div>

        <!-- Tags récapitulatifs -->
        <div v-if="serialNumbers.length > 0" class="sn-tags-section mt-3">
            <div class="d-flex align-items-center justify-content-between mb-2">
                <h3 class="h6 font-weight-bold text-dark mb-0">
                    <i class="far fa-list-check mr-1 text-primary"></i>
                    Numéros de série saisis
                    <span class="badge badge-primary ml-1">{{ serialNumbers.length }}</span>
                </h3>
                <button type="button" class="btn btn-sm btn-outline-danger" @click="clearAll">
                    <i class="far fa-trash-alt mr-1"></i> Tout effacer
                </button>
            </div>

            <div class="sn-tags-container">
                <span v-for="(sn, index) in serialNumbers" :key="sn.uniq" class="sn-tag" :class="{ 'sn-tag-error': getError(index, 'serial_number') }">
                    <i class="far fa-barcode-read sn-tag-icon"></i>
                    <span class="sn-tag-value">{{ sn.serial_number }}</span>
                    <i class="far fa-times sn-tag-remove" @click="removeSerial(index)"></i>
                </span>
            </div>

            <!-- Erreur backend par SN -->
            <div v-if="snFieldErrors.length > 0" class="sn-field-errors mt-2">
                <div v-for="err in snFieldErrors" :key="err" class="sn-field-error-item">
                    <i class="far fa-exclamation-circle mr-1"></i> {{ err }}
                </div>
            </div>
        </div>

        <!-- État vide -->
        <div v-else-if="snEnabled && stockValue > 0" class="text-muted small mt-3 py-2">
            <i class="far fa-info-circle mr-1"></i> Aucun numéro de série saisi pour le moment.
        </div>

        <!-- Hidden inputs for form submission -->
        <input
            type="hidden"
            v-for="(sn, index) in serialNumbers"
            :key="'sn_' + sn.uniq"
            :name="'serial_numbers[' + index + '][serial_number]'"
            :value="sn.serial_number"
        >
        <input
            type="hidden"
            v-for="(sn, index) in serialNumbers"
            :key="'id_' + sn.uniq"
            :name="'serial_numbers[' + index + '][id]'"
            :value="sn.id"
            v-if="sn.id"
        >
    </div>
</template>

<script>
export default {
    name: 'article-serial-number-form',
    props: {
        oldSerialNumbers: {
            type: String,
            default: '[]'
        },
        errors: {
            type: [Object, Array],
            default: () => ({})
        },
        stockInputId: {
            type: String,
            default: 'stock'
        }
    },
    data() {
        return {
            serialNumbers: [],
            scanValue: '',
            scanError: '',
            stockValue: 0,
            snEnabled: false
        };
    },
    computed: {
        progressPercent() {
            if (this.stockValue <= 0) return 0;
            return Math.min(100, (this.serialNumbers.length / Math.floor(this.stockValue)) * 100);
        },
        progressBarClass() {
            if (this.serialNumbers.length > Math.floor(this.stockValue)) return 'bg-warning';
            if (this.serialNumbers.length === Math.floor(this.stockValue)) return 'bg-success';
            return 'bg-primary';
        },
        generalErrors() {
            var errs = this.errors['serial_numbers'] || [];
            return Array.isArray(errs) ? errs : [errs];
        },
        snFieldErrors() {
            var errs = [];
            this.serialNumbers.forEach((sn, index) => {
                var e = this.getError(index, 'serial_number');
                if (e && !errs.includes(e)) errs.push(e);
            });
            return errs;
        }
    },
    watch: {
        'scanValue': function() {
            this.scanError = '';
        },
        'snEnabled': function(val) {
            if (val) {
                this.$nextTick(() => {
                    if (this.$refs.scanInput) this.$refs.scanInput.focus();
                });
            }
        }
    },
    methods: {
        addScannedSerial() {
            var value = this.scanValue.trim();
            if (!value || !this.snEnabled) return;

            var isDuplicate = this.serialNumbers.some(sn => sn.serial_number.toLowerCase() === value.toLowerCase());
            if (isDuplicate) {
                this.scanError = 'Ce numéro de série a déjà été saisi.';
                return;
            }

            this.serialNumbers.push({
                uniq: Math.random().toString(36).substr(2, 9),
                id: null,
                serial_number: value
            });

            this.scanValue = '';
            this.scanError = '';

            this.$nextTick(() => {
                if (this.$refs.scanInput) this.$refs.scanInput.focus();
            });
        },
        removeSerial(index) {
            this.serialNumbers.splice(index, 1);
        },
        clearAll() {
            this.serialNumbers = [];
        },
        getError(index, field) {
            const key = `serial_numbers.${index}.${field}`;
            return this.errors[key] ? this.errors[key][0] : null;
        },
        updateStockValue() {
            var el = document.getElementById(this.stockInputId);
            this.stockValue = el ? parseFloat(el.value) || 0 : 0;
        },
        updateSnEnabled() {
            var checked = document.querySelector('input[name="has_serial_numbers"]:checked');
            this.snEnabled = checked ? checked.value === '1' : false;
        },
        initializeSerialNumbers() {
            try {
                const old = JSON.parse(this.oldSerialNumbers);
                if (Array.isArray(old)) {
                    this.serialNumbers = old.filter(sn => sn.serial_number).map(sn => ({
                        uniq: Math.random().toString(36).substr(2, 9),
                        id: sn.id || null,
                        serial_number: sn.serial_number || ''
                    }));
                }
            } catch (e) {
                this.serialNumbers = [];
            }
        }
    },
    mounted() {
        this.updateStockValue();
        this.updateSnEnabled();
        this.initializeSerialNumbers();
        var self = this;
        var el = document.getElementById(this.stockInputId);
        if (el) {
            el.addEventListener('input', function() {
                self.updateStockValue();
            });
        }
        document.querySelectorAll('input[name="has_serial_numbers"]').forEach(function(radio) {
            radio.addEventListener('change', function() {
                self.updateSnEnabled();
            });
        });
    }
}
</script>

<style scoped>
.sn-scan-zone {
    margin-bottom: 8px;
}

.sn-scan-input {
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    font-size: 15px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.sn-scan-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.sn-scan-input.is-invalid {
    border-color: #dc3545;
}

.sn-scan-input:disabled {
    background-color: #f8fafc;
    cursor: not-allowed;
}

.sn-scan-error {
    color: #dc3545;
    font-size: 13px;
    margin-top: 6px;
}

.sn-progress-wrapper {
    margin-top: 12px;
}

.sn-tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    max-height: 280px;
    overflow-y: auto;
}

.sn-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px 6px 8px;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 13px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: all 0.15s ease;
}

.sn-tag:hover {
    border-color: #94a3b8;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.sn-tag-error {
    border-color: #fca5a5;
    background: #fef2f2;
}

.sn-tag-icon {
    color: #64748b;
    font-size: 14px;
    flex-shrink: 0;
}

.sn-tag-value {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
}

.sn-tag-remove {
    color: #94a3b8;
    cursor: pointer;
    font-size: 12px;
    padding: 2px;
    border-radius: 4px;
    transition: all 0.15s ease;
}

.sn-tag-remove:hover {
    color: #ef4444;
    background: #fef2f2;
}

.sn-backend-error-item {
    color: #dc3545;
    font-size: 13px;
    margin-bottom: 2px;
}

.sn-field-error-item {
    color: #dc3545;
    font-size: 12px;
    margin-bottom: 2px;
}
</style>
